/**
 * src/shell/modules/SovereignModuleSlotManager.ts
 *
 * Sovereign Reactive Module Slot Manager (Sprint 3 / Issue 3.1).
 * Maps enabled server-side & local modules to their declared UI slots (header, sidebar, footer, floating, room).
 * Provides a reactive subscription API for slot renderers across the application shell.
 */

import { ModuleSlotDeclaration, ModuleSlotId, ServerModuleManifest } from '../../../server/hosted-server/services/module-integrity-verifier.js';

export interface ModuleSlotEntry {
  moduleId: string;
  moduleName: string;
  version: string;
  slot: ModuleSlotId;
  label?: string;
  icon?: string;
  action?: string;
  guard?: string;
  entrypoint?: string;
  systemModule?: boolean;
}

type SlotSubscriber = (slotMap: Map<ModuleSlotId, ModuleSlotEntry[]>) => void;

export class SovereignModuleSlotManager {
  private static instance: SovereignModuleSlotManager;
  private slotMap: Map<ModuleSlotId, ModuleSlotEntry[]> = new Map();
  private subscribers: Set<SlotSubscriber> = new Set();
  private activeModules: ServerModuleManifest[] = [];
  private activeModalModuleId: string | null = null;
  private modalSubscribers: Set<(activeModuleId: string | null) => void> = new Set();

  private constructor() {
    this.slotMap.set('header', []);
    this.slotMap.set('sidebar', []);
    this.slotMap.set('footer', []);
    this.slotMap.set('floating', []);
    this.slotMap.set('room', []);
  }

  public static getInstance(): SovereignModuleSlotManager {
    if (!SovereignModuleSlotManager.instance) {
      SovereignModuleSlotManager.instance = new SovereignModuleSlotManager();
    }
    return SovereignModuleSlotManager.instance;
  }

  /**
   * Updates active module manifest declarations and recomputes slot index.
   */
  public updateActiveModules(modules: ServerModuleManifest[]): void {
    this.activeModules = modules;
    const newSlotMap = new Map<ModuleSlotId, ModuleSlotEntry[]>();
    newSlotMap.set('header', []);
    newSlotMap.set('sidebar', []);
    newSlotMap.set('footer', []);
    newSlotMap.set('floating', []);
    newSlotMap.set('room', []);

    for (const mod of modules) {
      if (mod.isEnabled === false) continue;

      if (Array.isArray(mod.slots)) {
        for (const slotDecl of mod.slots) {
          const list = newSlotMap.get(slotDecl.slot) || [];
          list.push({
            moduleId: mod.id,
            moduleName: mod.name,
            version: mod.version,
            slot: slotDecl.slot,
            label: slotDecl.label || mod.name,
            icon: slotDecl.icon || mod.icon,
            action: slotDecl.action,
            guard: slotDecl.guard,
            entrypoint: mod.entrypoint,
            systemModule: mod.systemModule,
          });
          newSlotMap.set(slotDecl.slot, list);
        }
      }
    }

    this.slotMap = newSlotMap;
    this.notify();
  }

  /**
   * Retrieves all registered slot entries for a specific UI slot.
   */
  public getSlots(slotId: ModuleSlotId): ModuleSlotEntry[] {
    return this.slotMap.get(slotId) || [];
  }

  /**
   * Get all registered slot entries across all slots.
   */
  public getAllSlots(): Map<ModuleSlotId, ModuleSlotEntry[]> {
    return new Map(this.slotMap);
  }

  /**
   * Subscribes to changes in slot mapping.
   */
  public subscribe(callback: SlotSubscriber): () => void {
    this.subscribers.add(callback);
    callback(this.slotMap);
    return () => {
      this.subscribers.delete(callback);
    };
  }

  /**
   * Modal trigger controls for floating / drawer micro-widgets.
   */
  public openModuleModal(moduleId: string): void {
    this.activeModalModuleId = moduleId;
    this.notifyModalSubscribers();
  }

  public closeModuleModal(): void {
    this.activeModalModuleId = null;
    this.notifyModalSubscribers();
  }

  public getActiveModalModuleId(): string | null {
    return this.activeModalModuleId;
  }

  public subscribeModal(callback: (activeModuleId: string | null) => void): () => void {
    this.modalSubscribers.add(callback);
    callback(this.activeModalModuleId);
    return () => {
      this.modalSubscribers.delete(callback);
    };
  }

  private notify(): void {
    this.subscribers.forEach((cb) => cb(this.slotMap));
  }

  private notifyModalSubscribers(): void {
    this.modalSubscribers.forEach((cb) => cb(this.activeModalModuleId));
  }
}

export const sovereignModuleSlotManager = SovereignModuleSlotManager.getInstance();
